import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

// One confirmation surface for the whole app.
//
// Before this, destructive actions were gated three different ways — a styled
// Radix dialog with a typed keyword (automations), a bare `window.confirm()`
// (11 sites: delete a KB article, delete a template, delete a group, demote
// yourself, cancel someone's approved leave…), and in several cases nothing at
// all. The severity ordering was also inverted: Resolve, which is reversible,
// opened a dialog, while Dismiss and Archive fired on a single click.
//
// Native `confirm()` isn't only a visual inconsistency. Chrome's "prevent this
// page from creating additional dialogs" checkbox makes every later call return
// false, so delete buttons silently stop working with no feedback at all.
//
// `requireTyping` is for the genuinely unrecoverable: the user must type an
// exact word before the confirm button enables.

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Plain-language consequence. Say what happens and whether it can be undone. */
  description: React.ReactNode;
  /** Names the action, never "OK" — the button should read as the verb. */
  confirmLabel: string;
  cancelLabel?: string;
  severity?: 'warn' | 'destructive';
  /** When set, the confirm button stays disabled until this exact string is typed. */
  requireTyping?: string;
  busy?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  severity = 'warn',
  requireTyping,
  busy = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = React.useState('');

  // Reset the typed guard whenever the dialog reopens, so a previous
  // confirmation can't leave the button pre-armed.
  React.useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const armed = !requireTyping || typed.trim() === requireTyping;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              className={cn(
                'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                severity === 'destructive'
                  ? 'bg-tint-critical text-tint-critical-fg'
                  : 'bg-tint-warning text-tint-warning-fg',
              )}
            >
              <AlertTriangle className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription className="mt-1.5">{description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {requireTyping ? (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-typed-guard">
              Type <span className="font-mono font-semibold">{requireTyping}</span> to confirm
            </Label>
            <Input
              id="confirm-typed-guard"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-describedby="confirm-typed-guard-hint"
            />
            <p id="confirm-typed-guard-hint" className="text-2xs text-muted-foreground">
              This can't be undone, so we ask you to type it out.
            </p>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={severity === 'destructive' ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={busy || !armed}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Imperative wrapper for call sites that previously used `window.confirm()`
 * inline inside an event handler. Renders nothing until asked.
 *
 *   const confirm = useConfirm();
 *   …
 *   if (!(await confirm({ title: 'Delete "Welcome"?', … }))) return;
 *
 * Returns a promise that resolves true on confirm, false on cancel/dismiss —
 * the same shape the native call had, so migrating a site is a one-line change
 * plus rendering `{confirmUi}` somewhere in the component.
 */
type ConfirmRequest = Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'onConfirm' | 'busy'>;

export function useConfirm(): {
  confirm: (req: ConfirmRequest) => Promise<boolean>;
  confirmUi: React.ReactNode;
} {
  const [request, setRequest] = React.useState<ConfirmRequest | null>(null);
  const resolverRef = React.useRef<((ok: boolean) => void) | null>(null);

  const confirm = React.useCallback((req: ConfirmRequest) => {
    setRequest(req);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = React.useCallback((ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setRequest(null);
  }, []);

  const confirmUi = request ? (
    <ConfirmDialog
      {...request}
      open
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
      onConfirm={() => settle(true)}
    />
  ) : null;

  return { confirm, confirmUi };
}
